// Single source of truth for the Resources area.
//
// The sidebar tree (ResourceNav.vue), the generic pages (ResourcePage.vue) and the
// router are all generated from this structure, so content changes only happen here.
//
// Node shape:
//   key       - url segment (unique among siblings)
//   title     - display label
//   icon      - semantic-ui icon class (optional), e.g. 'heart' or 'folder open'
//   iconColor - semantic-ui color for the icon (optional)
//   blurb     - short intro shown on the node's own page (optional)
//   to        - internal route path (grouping / landing nodes)
//   href      - external URL or /static file (leaf links, opened in a new tab)
//   fileType  - icon hint for href leaves: 'word' | 'powerpoint' | 'video' (optional)
//   note      - badge text; renders the node as non-clickable text (e.g. 'TBC')
//   component - 'stage' to render CooperativeLearningStage instead of ResourcePage
//   stageKey  - key into src/data/cooperativeLearningStages.js (with component: 'stage')
//   children  - child nodes

const PRIMARY = '/static/resources/primary';
const POSTPRIMARY = '/static/resources/postprimary';

/* eslint-disable max-len */
const tree = {
  key: 'resources',
  title: 'Resources',
  icon: 'folder',
  iconColor: 'blue',
  to: '/resources',
  blurb: 'Browse the Helping Hands teaching resources for primary and post primary settings.',
  children: [
    {
      key: 'primary',
      title: 'Primary Resources',
      icon: 'child',
      iconColor: 'teal',
      to: '/resources/primary',
      blurb: 'Resources for primary schools.',
      children: [
        {
          key: 'cooperative-learning',
          title: 'Cooperative Learning Links',
          icon: 'handshake outline',
          iconColor: 'green',
          to: '/resources/primary/cooperative-learning',
          blurb: 'Video and discussion links to use alongside each Cooperative Learning unit.',
          children: [
            {
              key: 'stage-1',
              title: 'Stage 1',
              to: '/resources/primary/cooperative-learning/stage-1',
              component: 'stage',
              stageKey: 'primaryStage1',
            },
          ],
        },
        {
          key: 'kindness-report',
          title: 'Kindness Report',
          icon: 'heart',
          iconColor: 'pink',
          to: '/resources/primary/kindness-report',
          blurb: 'Guidance and templates for running the Kindness Report in primary schools.',
          children: [
            {
              key: 'guidelines-for-teachers',
              title: 'Kindness Report - Guidelines for Teachers',
              href: `${PRIMARY}/HH Kindness Report -Guidelines for teachers.docx`,
              fileType: 'word',
            },
            {
              key: 'letter-to-parents',
              title: 'Kindness Report - Letter to Parents',
              href: `${PRIMARY}/HH Kindness report Letter to Parents.docx`,
              fileType: 'word',
            },
            {
              key: 'primary',
              title: 'Kindness Report - Primary',
              href: `${PRIMARY}/HH Kindness Report Primary.docx`,
              fileType: 'word',
            },
          ],
        },
        {
          key: 'classroom-teams',
          title: 'Classroom Teams',
          icon: 'users',
          iconColor: 'orange',
          to: '/resources/primary/classroom-teams',
          blurb: 'Approaches for forming and running classroom teams.',
          children: [
            {
              key: 'clock-method',
              title: 'Clock Method',
              href: `${PRIMARY}/Clock method for ad hoc Teams.pptx`,
              fileType: 'powerpoint',
            },
            {
              key: 'forming-a-team',
              title: 'Forming a team',
              href: `${PRIMARY}/Forming a team.pptx`,
              fileType: 'powerpoint',
            },
          ],
        },
        {
          key: 'empathic-interviewing',
          title: 'Empathic Interviewing',
          icon: 'comments',
          iconColor: 'purple',
          to: '/resources/primary/empathic-interviewing',
          blurb: 'Guidance for conducting empathic interviews.',
          children: [
            {
              key: 'empathic-interview-guidelines',
              title: 'Empathic Interview Guidelines',
              href: `${PRIMARY}/Empathic Interview Guidelines.docx`,
              fileType: 'word',
            },
          ],
        },
        {
          key: 'parents',
          title: 'Parents',
          icon: 'users',
          iconColor: 'green',
          to: '/resources/primary/parents',
          blurb: 'Information to share with parents.',
          children: [
            {
              key: 'parents-with-voiceover',
              title: 'Parents with VoiceOver',
              href: `${PRIMARY}/Parents with Voice over.pptx`,
              fileType: 'powerpoint',
            },
            {
              key: 'helping-hands-parents-leaflet',
              title: 'Helping Hands Parents Information Leaflet',
              href: `${PRIMARY}/Helping Hands Parent Information Leaflet.docx`,
              fileType: 'word',
            },
          ],
        },
        {
          key: 'additional',
          title: 'Additional Resources',
          icon: 'folder open',
          iconColor: 'grey',
          to: '/resources/primary/additional',
          blurb: 'Further supporting material.',
          children: [
            {
              key: 'bi-cinnealta-policy',
              title: 'For inclusion in Schools Bí Cinnealta Policy',
              href: `${PRIMARY}/For inclusion in schools Bi Cinealta Policy.docx`,
              fileType: 'word',
            },
            {
              key: 'core-team-class-plan-template',
              title: 'Core Team Class Plan template',
              href: `${PRIMARY}/Core Team Class Plan template.docx`,
              fileType: 'word',
            },
          ],
        },
      ],
    },
    {
      key: 'post-primary',
      title: 'Post Primary Resources',
      icon: 'graduation cap',
      iconColor: 'teal',
      to: '/resources/post-primary',
      blurb: 'Resources for post primary schools.',
      children: [
        {
          key: 'cooperative-learning',
          title: 'Cooperative Learning Links',
          icon: 'handshake outline',
          iconColor: 'green',
          to: '/resources/post-primary/cooperative-learning',
          blurb: 'Video and discussion links to use alongside each Cooperative Learning unit.',
          children: [
            {
              key: 'stage-1',
              title: 'Stage 1',
              to: '/resources/post-primary/cooperative-learning/stage-1',
              component: 'stage',
              stageKey: 'ppStage1',
            },
            {
              key: 'stage-2',
              title: 'Stage 2',
              to: '/resources/post-primary/cooperative-learning/stage-2',
              component: 'stage',
              stageKey: 'ppStage2',
            },
          ],
        },
        {
          key: 'positive-actions',
          title: 'Positive Actions',
          icon: 'heart',
          iconColor: 'pink',
          to: '/resources/post-primary/positive-actions',
          blurb: 'Guidance and templates for running Positive Actions in post primary schools.',
          children: [
            {
              key: 'guidelines-for-teachers',
              title: 'Positive Actions - Guidelines for Teachers',
              href: `${POSTPRIMARY}/HH Positive Actions (Guidelines for teachers).docx`,
              fileType: 'word',
            },
            {
              key: 'letter-to-parents',
              title: 'Positive Actions - Letter to Parents',
              href: `${POSTPRIMARY}/HH Positive Actions Letter to parents PP.docx`,
              fileType: 'word',
            },
            {
              key: 'report-post-primary',
              title: 'Positive Actions - Report Post Primary',
              href: `${POSTPRIMARY}/HH Positive Actions Report Post Primary.docx`,
              fileType: 'word',
            },
          ],
        },
        {
          key: 'classroom-teams',
          title: 'Classroom Teams',
          icon: 'users',
          iconColor: 'orange',
          to: '/resources/post-primary/classroom-teams',
          blurb: 'Approaches for forming and running classroom teams.',
          children: [
            {
              key: 'clock-method',
              title: 'Clock Method',
              href: `${POSTPRIMARY}/Clock method for ad hoc Teams.pptx`,
              fileType: 'powerpoint',
            },
            {
              key: 'forming-a-team',
              title: 'Forming a team',
              href: `${PRIMARY}/Forming a team.pptx`,
              fileType: 'powerpoint',
            },
          ],
        },
        {
          key: 'empathic-interviewing',
          title: 'Empathic Interviewing',
          icon: 'comments',
          iconColor: 'purple',
          to: '/resources/post-primary/empathic-interviewing',
          blurb: 'Guidance for conducting empathic interviews.',
          children: [
            {
              key: 'empathic-interview-guidelines',
              title: 'Empathic Interview Guidelines',
              href: `${POSTPRIMARY}/Empathic Interview Guidelines.docx`,
              fileType: 'word',
            },
          ],
        },
        {
          key: 'parents',
          title: 'Parents',
          icon: 'users',
          iconColor: 'green',
          to: '/resources/post-primary/parents',
          blurb: 'Information to share with parents.',
          children: [
            {
              key: 'parents-with-voiceover',
              title: 'Parents with VoiceOver',
              href: `${POSTPRIMARY}/Parents with Voice over.pptx`,
              fileType: 'powerpoint',
            },
            {
              key: 'helping-hands-parents-leaflet',
              title: 'Helping Hands Parents Information Leaflet',
              href: `${POSTPRIMARY}/Helping Hands Parent Information Leaflet.docx`,
              fileType: 'word',
            },
          ],
        },
        {
          key: 'additional',
          title: 'Additional Resources',
          icon: 'folder open',
          iconColor: 'grey',
          to: '/resources/post-primary/additional',
          blurb: 'Further supporting material.',
          children: [
            {
              key: 'bi-cinnealta-policy',
              title: 'For inclusion in Schools Bí Cinnealta Policy',
              href: `${POSTPRIMARY}/For inclusion in schools Bi Cinealta Policy.docx`,
              fileType: 'word',
            },
            {
              key: 'teamwork-cl-approach',
              title: 'Teamwork: A CL Approach aligned with SPHE and Wellbeing guidelines',
              href: `${POSTPRIMARY}/Teamwork A CL Approach aligned with SPHE and Wellbeing guidelines.docx`,
              fileType: 'word',
            },
            {
              key: 'core-team-class-plan-template',
              title: 'Core Team Class Plan template',
              href: `${POSTPRIMARY}/Core Team Class Plan template.docx`,
              fileType: 'word',
            },
          ],
        },
      ],
    },
  ],
};
/* eslint-enable max-len */

// Build lookup / route helpers by walking the tree once.
const nodesByPath = {};
const routes = [];

function walk(node, ancestors) {
  const trail = ancestors.concat(node);
  if (node.to) {
    nodesByPath[node.to] = { node, ancestors };
    routes.push(node);
  }
  if (node.children) {
    node.children.forEach(child => walk(child, trail));
  }
}
walk(tree, []);

export const resourcesTree = tree;

// Return the node registered at an internal route path, or null.
export function findNode(path) {
  const entry = nodesByPath[path];
  return entry ? entry.node : null;
}

// Return the chain of ancestor nodes (root first) for an internal route path.
export function findAncestors(path) {
  const entry = nodesByPath[path];
  return entry ? entry.ancestors : [];
}

// Every node that owns an internal route, for router generation.
export function flattenRoutes() {
  return routes;
}



// WEBPACK FOOTER //
// ./src/data/resources.js